import { forwardRef } from 'react';
import type { ComponentProps } from 'react';
import type { LucideIcon } from 'lucide-react';

/**
 * Icon — kit primitive wrapping lucide-react icons so size follows text
 * (DESIGN.md §3.6). lucide's `size` prop is written straight through to the
 * SVG `width`/`height` attributes (verified against
 * node_modules/lucide-react/dist/esm/Icon.js: `width: size ?? contextSize`).
 * Passing a string like "1.25em" survives unchanged — SVG2 maps the root
 * `<svg>` element's width/height presentation attributes to the CSS
 * properties of the same name, which resolve em against font-size. No
 * style-object workaround is needed.
 *
 * Named em steps (the single definition — do not duplicate elsewhere):
 *   xs = .75em   sm = .875em   md = 1em (default)   lg = 1.25em   xl = 1.5em
 *
 * Escape hatch: `em={n}` for one-off numeric values the sweep needs to match
 * a legacy px pairing exactly (e.g. a 14px icon in 13px text = 1.0769em).
 * `em` wins over `size` when both are given.
 */

export type IconStep = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export const ICON_STEP_EM: Record<IconStep, number> = {
  xs: 0.75,
  sm: 0.875,
  md: 1,
  lg: 1.25,
  xl: 1.5,
};

export interface IconProps extends Omit<ComponentProps<LucideIcon>, 'size'> {
  /** The lucide icon component, e.g. `icon={Plus}`. */
  icon: LucideIcon;
  /** Named em step. Default 'md' (1em). Ignored if `em` is set. */
  size?: IconStep;
  /** Escape hatch: exact numeric em value for a one-off legacy match. */
  em?: number;
}

export const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon(
  { icon: LucideIconComponent, size = 'md', em, ...rest },
  ref
) {
  // Chromium stores lengths in 1/64px LayoutUnits and truncates: a 4-decimal
  // em ratio (1.0769em × 13px = 13.9997px) lands 1/64px short of the legacy
  // px size and the shortfall accumulates across a toolbar of icons (proven by
  // the picture-compare gate). A +0.0003em nudge (≤ 0.008px at 28px) lifts the
  // truncation back onto the intended pixel without ever crossing the next one.
  const emValue = em != null ? em + 0.0003 : ICON_STEP_EM[size];
  return <LucideIconComponent ref={ref} size={`${emValue}em`} {...rest} />;
});

/**
 * Maps a legacy (px, contextPx) pairing to the closest named step, or an
 * exact `em` value when no step is a close-enough match (tolerance: 1%
 * relative difference — tight enough that near-miss legacy pairings like
 * 16px-in-13px-text (1.5625% off `lg`) stay pinned to their exact em rather
 * than silently drifting to the nearest step).
 */
export function iconSizeForPx(px: number, contextPx: number): { size: IconStep } | { em: number } {
  const ratio = px / contextPx;
  let bestStep: IconStep | undefined;
  let bestDiff = Infinity;
  for (const step of Object.keys(ICON_STEP_EM) as IconStep[]) {
    const diff = Math.abs(ICON_STEP_EM[step] - ratio) / ratio;
    if (diff < bestDiff) {
      bestDiff = diff;
      bestStep = step;
    }
  }
  if (bestStep && bestDiff <= 0.01) {
    return { size: bestStep };
  }
  return { em: Math.round(ratio * 10000) / 10000 };
}
